import type { ReactNode } from "react";
import type { UseQueryResult } from "@tanstack/react-query";
import { friendlyError } from "@/lib/errors";
import { ErrorState, TableSkeleton } from "./states";

/**
 * Carga, error y vacío en un solo envoltorio.
 *
 * Sin esto, cada pantalla repite el mismo trio de `if` y acaban divergiendo:
 * una muestra un spinner, otra no muestra nada y una tercera enseña el error
 * crudo de PostgreSQL.
 */
export function QueryBoundary<T>({
  query,
  skeleton,
  empty,
  isEmpty,
  children,
}: {
  query: UseQueryResult<T>;
  skeleton?: ReactNode;
  empty?: ReactNode;
  isEmpty?: (data: T) => boolean;
  children: (data: T) => ReactNode;
}) {
  if (query.isPending) {
    return <>{skeleton ?? <TableSkeleton />}</>;
  }

  if (query.isError) {
    return (
      <ErrorState
        message={friendlyError(query.error)}
        onRetry={() => void query.refetch()}
      />
    );
  }

  const data = query.data as T;

  if (empty && isEmpty?.(data)) {
    return <>{empty}</>;
  }

  return <>{children(data)}</>;
}
